import { customAlphabet } from "nanoid";
import { TModuleOptions, TMovementElementItem, TTaxResume } from "../models/salesModel";
import dayjs from "dayjs";

export function createId(length:number){
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-_"
  const nanoid = customAlphabet(alphabet,length)
  let str = nanoid(length)

  if(str.startsWith('-') || str.startsWith('_')){
    str = 'A' + str.slice(1);
  }
  if(str.endsWith('-') || str.endsWith('_')){
    str = str.slice(0, -1) + 'z';
  }
  return str
}

export function roundValue(value: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function addToUniquesArray(array:any[],element:any,id:any,position:'start' | 'end' = 'end'){
  array = array.filter((item: any) => !JSON.stringify(item).includes(id));
  if (position === 'start') {
    array.unshift(element);
  } else {
    array.push(element);
  }
  return array;
}

export function removeDuplicatesByKey(array:any, key:any) {
  const seenKeys = new Set();
  return array.filter((item:any) => {
    const keyValue = item[key];
    if (seenKeys.has(keyValue)) {
      return false; 
    }
    seenKeys.add(keyValue);
    return true; 
  });
}

export function getTotalTaxes(itemList: TMovementElementItem[]): TTaxResume[] {
  const taxMap = new Map<string, TTaxResume>();
  itemList.forEach(item => {
    if (item.taxes_list) {
      item.taxes_list.forEach(tax => {
        if (taxMap.has(tax.key)) {
          const existing = taxMap.get(tax.key)!;
          taxMap.set(tax.key, {
            ...existing,
            tax_amount: existing.tax_amount + tax.tax_amount
          });
        } else {
          taxMap.set(tax.key, { ...tax });
        }
      });
    }
  });
  return Array.from(taxMap.values());
} 

export function getItemTaxes(item: TMovementElementItem, taxesOptions: TModuleOptions[]) {
  return item.module_options
    .filter(el => el.sub_type === "SELL_TAX" && el.is_active)
    .map(taxModuleOption => {
      const taxConfig = taxesOptions.find(el => el.id === taxModuleOption.option_id);
      if (!taxConfig) return null;

      const tax_amount = taxConfig.type === "TAX_RATE" ?
       (taxConfig.numeric_value || 0) / 100 * ((item.quantity * item.unit_price)*(1-item.discount_rate/100)):
       taxConfig.numeric_value || 0;
      return {
        id: taxConfig.id || 0,
        key: taxConfig.key || "",
        tax_amount: roundValue(tax_amount) || 0,
        description: taxConfig.string_value || ""
      } as TTaxResume;
    })
    .filter((tax): tax is TTaxResume => tax !== null);
}

export function textNumberFormater(text:string|number,format:string='money'){
  if(text===0) text = "0"
  text = text || ""
  if(format === "money"){
    return text.toLocaleString("es-CO")
  }
  return text.toString()
}

export function customNanoId(size:number=10){
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const nanoid = customAlphabet(alphabet, 10);
  return nanoid(size)
}

/**
 * Formatea los valores de un input numerico para convertirlo a separadores de mil por puntos y de coma para decimales
 * @param value 
 * @returns 
 */
export function formatInputNumber(value:number|undefined) {
  if (!value) return "";
  const [integerPart, decimalPart] = value.toString().split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decimalPart ? `${formattedInteger},${decimalPart}` : formattedInteger;
};

/**
 * Formatea los valores de un input numerico para convertirlo al valor estandar de Javascript
 * @param value 
 * @returns 
 */
export function parseInputNumber(value:any){
  if (!value) return "";
  return value.replace(/\./g, "").replace(",", ".");
};

/**
 * Resize long texts and add ...
 * @param text
 * @param maxLength
 * @returns 
 */
export function resizeText(text:any, maxLength:number) {
  if (text.length > maxLength) {
      return text.slice(0, maxLength) + "...";
  }
  return text;
}

export function getDateFilter(storedValues?: string[]) {
  if (storedValues?.[0] && storedValues[1]) {
    return {
      startDate: dayjs(storedValues[0]).startOf('day').valueOf(),
      endDate: dayjs(storedValues[1]).endOf('day').valueOf(),
    };
  } else {
    return {
      startDate: dayjs().startOf('day').valueOf(),
      endDate: dayjs().endOf('day').valueOf(),
    };
  }
}

export function getDocumentLabels(type: string | undefined, invoiceType: string | undefined) {
  if (invoiceType === 'CREDIT_NOTE' || type === 'CREDIT_NOTE') {
    return { title: 'Nota Crédito', counterparty: 'Cliente:', user: 'Vendedor:' };
  }
  if (invoiceType === 'DEBIT_NOTE' || type === 'DEBIT_NOTE') {
    return { title: 'Nota Débito', counterparty: 'Cliente:', user: 'Vendedor:' };
  }

  switch (type) {
    case 'SALE_INVOICE':
      return { title: 'Comprobante de Venta', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'QUOTATION':
      return { title: 'Cotización', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'PURCHASE_RECEIPT':
      return { title: 'Comprobante de Compra', counterparty: 'Proveedor:', user: 'Recibido por:' };
    case 'RETURN_IN':
      return { title: 'Devolución en Venta', counterparty: 'Cliente:', user: 'Recibido por:' };
    default:
      return { title: 'Comprobante', counterparty: 'Tercero:', user: 'Usuario:' };
  }
}

/**
 * Formats a date/timestamp to a standard UI display string (default 'DD/MM/YYYY hh:mm A')
 */
export function formatDateTime(dateStr?: string | number | Date | null, format: string = 'DD/MM/YYYY hh:mm A'): string {
  if (!dateStr) return '-';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format(format) : '-';
}

/**
 * Formats a date to 'DD/MM/YYYY' for UI display
 */
export function formatDate(dateStr?: string | number | Date | null, format: string = 'DD/MM/YYYY'): string {
  return formatDateTime(dateStr, format);
}

/**
 * Determines if a credit note reason corresponds to full annulment or total return
 */
export function isFullReturnOrAnnulment(reasonKey?: string, legalCode?: string, reasonText?: string): boolean {
  const k = (reasonKey || "").toUpperCase().trim();
  const c = (legalCode || "").trim();
  const t = (reasonText || "").toLowerCase().trim();

  if (['ANLN', 'ANULACION', 'DEVOLUCION_TOTAL'].includes(k)) {
    return true;
  }
  if (c === '2') {
    return true;
  }
  if (c === '1' && (t.includes('total') || k.includes('TOTAL'))) {
    return true;
  }
  if (t.includes('anulac') || t.includes('devolución total') || t.includes('devolucion total')) {
    return true;
  }
  return false;
}

/**
 * Determines if a credit note reason corresponds to a physical return of items to warehouse
 * (both full annulment / total return and partial return of goods)
 */
export function isPhysicalReturn(reasonKey?: string, legalCode?: string, reasonText?: string): boolean {
  if (isFullReturnOrAnnulment(reasonKey, legalCode, reasonText)) {
    return true;
  }

  const k = (reasonKey || "").toUpperCase().trim();
  const c = (legalCode || "").trim();
  const t = (reasonText || "").toLowerCase().trim();

  // Explicit non-return / financial keys
  if (['AJUSTE_PRECIO', 'REBAJA_DESCUENTO', 'DSCN', 'AJST', 'PTPG', 'DSVN', 'DESC', 'PRIC'].includes(k)) {
    return false;
  }
  // Codes 3 (rebaja/descuento), 4 (ajuste precio), 5 (pronto pago), 6 (volumen) without return text
  if (['3', '4', '5', '6'].includes(c) && !t.includes('devoluc') && !t.includes('retorno')) {
    return false;
  }

  // Explicit physical return keys or code 1
  if (['DEVOLUCION_PARCIAL', 'DEVN', 'DEVOLUCION'].includes(k)) {
    return true;
  }
  if (c === '1') {
    return true;
  }
  if (t.includes('devolución') || t.includes('devolucion') || t.includes('no aceptación') || t.includes('no aceptacion') || t.includes('retorno')) {
    return true;
  }

  return false;
}

/**
 * Determines if a credit note reason is purely financial (price adjustment, rebate, discount)
 * without physical goods return.
 */
export function isFinancialAdjustment(reasonKey?: string, legalCode?: string, reasonText?: string): boolean {
  return !isPhysicalReturn(reasonKey, legalCode, reasonText);
}